/**
 * SportLandingsPanel.js — Phase D
 * AD-facing UI to edit per-sport landing-page blocks. The WP theme
 * reads these from /api/website-settings/{org_id} and renders them on
 * /baseball, /basketball, /volleyball.
 *
 * Backend: /api/sport-landing/{org_id}/{sport_slug}  (PUT upserts a
 * full block; bumps website_settings.version_stamp so the WP poller
 * picks it up on the next tick).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LiveSiteHeader from './LiveSiteHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = [
  { slug: 'baseball',   label: 'Baseball' },
  { slug: 'basketball', label: 'Basketball' },
  { slug: 'volleyball', label: 'Volleyball' },
];

const EMPTY = {
  hero_image_url: '',
  intro_paragraph: '',
  program_highlights: [],
  coach_callouts: [],
  schedule_cta_label: 'View Full Schedule',
  schedule_cta_url: '',
  visible: true,
};

export default function SportLandingsPanel({ orgId }) {
  const [sport, setSport] = useState(SPORTS[0].slug);
  const [block, setBlock] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    let stale = false;
    setLoading(true);
    axios.get(`${API}/sport-landing/${orgId}/${sport}`, { withCredentials: true })
      .then(({ data }) => {
        if (stale) return;
        if (data && data._unset) {
          setBlock({ ...EMPTY });
        } else {
          setBlock({
            hero_image_url:    data.hero_image_url || '',
            intro_paragraph:   data.intro_paragraph || '',
            program_highlights: data.program_highlights || [],
            coach_callouts:     data.coach_callouts || [],
            schedule_cta_label: data.schedule_cta_label || 'View Full Schedule',
            schedule_cta_url:   data.schedule_cta_url || '',
            visible:            data.visible !== false,
          });
        }
      })
      .catch(() => setBlock({ ...EMPTY }))
      .finally(() => !stale && setLoading(false));
    return () => { stale = true; };
  }, [orgId, sport]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/sport-landing/${orgId}/${sport}`,
        block,
        { withCredentials: true },
      );
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setBlock((b) => ({ ...b, [k]: v }));
  const setHighlight = (i, v) =>
    setBlock((b) => {
      const next = [...b.program_highlights];
      next[i] = v;
      return { ...b, program_highlights: next };
    });
  const addHighlight = () =>
    setBlock((b) => ({ ...b, program_highlights: [...b.program_highlights, ''] }));
  const removeHighlight = (i) =>
    setBlock((b) => ({
      ...b,
      program_highlights: b.program_highlights.filter((_, idx) => idx !== i),
    }));

  const setCallout = (i, k, v) =>
    setBlock((b) => {
      const next = [...b.coach_callouts];
      next[i] = { ...(next[i] || {}), [k]: v };
      return { ...b, coach_callouts: next };
    });
  const addCallout = () =>
    setBlock((b) => ({
      ...b,
      coach_callouts: [...b.coach_callouts, { name: '', role: '', quote: '' }],
    }));
  const removeCallout = (i) =>
    setBlock((b) => ({
      ...b,
      coach_callouts: b.coach_callouts.filter((_, idx) => idx !== i),
    }));

  return (
    <div data-testid="sport-landings-panel" className="p-6 space-y-5">
      <LiveSiteHeader orgId={orgId} surfaceLabel={`Sport landing — ${sport}`}
                       wpPath={`/${sport}`} />
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Sport Landing Pages</h2>
          <p className="text-sm text-gray-500">
            Edit the per-sport landing pages served from
            <code className="mx-1">/api/website-settings/{`{org}`}</code>
            and rendered by the WP theme on <code>/baseball</code>,
            <code> /basketball</code>, <code> /volleyball</code>.
          </p>
        </div>
        <select
          data-testid="sport-landings-select-sport"
          value={sport}
          onChange={(e) => setSport(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        >
          {SPORTS.map((s) => (
            <option key={s.slug} value={s.slug}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading && <div className="text-gray-500 text-sm">Loading…</div>}

      {!loading && (
        <>
          <Field label="Hero image URL">
            <input
              data-testid="sport-landings-hero-url"
              value={block.hero_image_url}
              onChange={(e) => set('hero_image_url', e.target.value)}
              placeholder="https://…"
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Intro paragraph">
            <textarea
              data-testid="sport-landings-intro"
              value={block.intro_paragraph}
              onChange={(e) => set('intro_paragraph', e.target.value)}
              rows={4}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Program highlights">
            <div className="space-y-2" data-testid="sport-landings-highlights">
              {block.program_highlights.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`sport-landings-highlight-${i}`}
                    value={h}
                    onChange={(e) => setHighlight(i, e.target.value)}
                    placeholder={`Highlight ${i + 1}`}
                    className="flex-1 border rounded px-3 py-2 text-sm"
                  />
                  <button
                    data-testid={`sport-landings-highlight-remove-${i}`}
                    onClick={() => removeHighlight(i)}
                    className="px-2 text-red-600 border border-red-200 rounded text-xs hover:bg-red-50"
                  >×</button>
                </div>
              ))}
              <button
                data-testid="sport-landings-highlight-add"
                onClick={addHighlight}
                className="px-3 py-1 text-xs border rounded hover:bg-gray-50"
              >+ Add highlight</button>
            </div>
          </Field>

          <Field label="Coach callouts">
            <div className="space-y-3" data-testid="sport-landings-callouts">
              {block.coach_callouts.map((c, i) => (
                <div key={i} className="border rounded p-3 grid grid-cols-12 gap-2"
                     data-testid={`sport-landings-callout-${i}`}>
                  <input
                    value={c.name || ''}
                    onChange={(e) => setCallout(i, 'name', e.target.value)}
                    placeholder="Coach name"
                    className="col-span-4 border rounded px-2 py-1 text-sm"
                  />
                  <input
                    value={c.role || ''}
                    onChange={(e) => setCallout(i, 'role', e.target.value)}
                    placeholder="Role (e.g. Varsity Head Coach)"
                    className="col-span-4 border rounded px-2 py-1 text-sm"
                  />
                  <input
                    value={c.headshot_url || ''}
                    onChange={(e) => setCallout(i, 'headshot_url', e.target.value)}
                    placeholder="Headshot URL"
                    className="col-span-3 border rounded px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => removeCallout(i)}
                    className="col-span-1 px-2 text-red-600 border border-red-200 rounded text-xs hover:bg-red-50"
                  >×</button>
                  <textarea
                    value={c.quote || ''}
                    onChange={(e) => setCallout(i, 'quote', e.target.value)}
                    placeholder="Pull quote"
                    rows={2}
                    className="col-span-12 border rounded px-2 py-1 text-sm"
                  />
                </div>
              ))}
              <button
                data-testid="sport-landings-callout-add"
                onClick={addCallout}
                className="px-3 py-1 text-xs border rounded hover:bg-gray-50"
              >+ Add callout</button>
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Schedule CTA label">
              <input
                data-testid="sport-landings-cta-label"
                value={block.schedule_cta_label}
                onChange={(e) => set('schedule_cta_label', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Schedule CTA URL">
              <input
                data-testid="sport-landings-cta-url"
                value={block.schedule_cta_url}
                onChange={(e) => set('schedule_cta_url', e.target.value)}
                placeholder="https://…"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </Field>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="sport-landings-visible"
                type="checkbox"
                checked={!!block.visible}
                onChange={(e) => set('visible', e.target.checked)}
              />
              Visible on the public site
            </label>
            <div className="flex-1" />
            {savedAt && (
              <span className="text-xs text-green-600" data-testid="sport-landings-saved-flash">
                ✓ Saved at {savedAt.toLocaleTimeString()}
              </span>
            )}
            <button
              data-testid="sport-landings-save"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
            >{saving ? 'Saving…' : 'Save & Publish'}</button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}
